import { adapter } from '@seek/tealium-adapter';

import type { VerificationsSeekPassDialogAnalyticsOrigin } from 'src/components/Profile/Verifications/analyticsUtils';
import { PROFILE_PAGE } from 'src/components/Router';
import { ANALYTICS_DEBUG } from 'src/config';
import { REJECT } from 'src/consts';
import type { AnalyticsEditLoadedType } from 'src/middlewares/createAnalyticsMiddleware';
import { CredentialExpiryStatus } from 'src/types/graphql';

import {
  createCertsyAnalytics,
  mapExpiryStatusToCertsyStatusAnalytics,
} from './certsy';
import { actionOriginReducer } from './reducers/actionOrigin';
import { type PageState, pageReducer } from './reducers/page';
import { profileReducer, type ProfileState } from './reducers/profile';
import { userReducer, type UserProps } from './reducers/user';
import { createStore } from './store';
import type {
  AnalyticsState,
  AnalyticsService,
  AnalyticsActionOrigin,
  AnalyticsCurrentPageSection,
  AnalyticsCurrentPageSectionState,
  AnalyticsUpdateType,
  AnalyticsViewData,
  ManageResumeUpdateProps,
  CreateResumeDisplayed,
  AnalyticsLinkEvent,
  AnalyticsProfileVisibilityLevel,
  AnalyticsLinkPosition,
  AnalyticsHeaderFooterLinkProps,
  AnalyticsPage,
  AnalyticsNudgeType,
  AnalyticsCallToAction,
  AnalyticsViewEvent,
  AnalyticsProfileSharedStatus,
  AnalyticsCallToActionType,
  AnalyticsResumeType,
  AnalyticsProfileInsightsProps,
  AnalyticsProfileSuggestionsProps,
  AnalyticsVerificationsWidgetName,
  AnalyticsVerificationsWidgetCallToAction,
  AnalyticsVerificationsWidgetLinkText,
  AnalyticsVerificationsCurrentPageSubsection,
  AnalyticsVerifiableCredentialType,
  AnalyticsVerificationsSeekPassDialogCallToAction,
  AnalyticsSensitiveInformationLinkText,
  AnalyticsResumeAlertShownType,
  AnalyticsResumeAlertShownTone,
  AnalyticsResumeAlertPosition,
  AnalyticsResumeAlertTitle,
  AnalyticsResumeAlertShownMessage,
} from './types';
import type { CardOpenProps } from './types/cardOpened';
import type {
  UpdateCardProps,
  UpdateUnconfirmedCardProps,
} from './types/updateCard';

export const PREVIOUS_PAGE_APPLY = 'applySuccess';

const defaultState: Pick<AnalyticsState, 'currentPage'> = {
  currentPage: 'profile',
};

const tealium = adapter();

if (typeof window !== 'undefined') {
  // `window.ANALYTICS_DEBUG` is used by `@seek/tealium-adapter`
  window.ANALYTICS_DEBUG = ANALYTICS_DEBUG;
}

export const getProfileSharedStatus = ({
  isShareableProfileCreated,
  isShareableProfileActive,
}: {
  isShareableProfileCreated: boolean;
  isShareableProfileActive: boolean;
}) => {
  if (!isShareableProfileCreated) {
    return 'not generated';
  }
  if (isShareableProfileActive) {
    return 'on';
  }
  return 'off';
};

const createAnalytics = (
  initialState: AnalyticsState = defaultState,
  analyticsService: AnalyticsService = tealium,
) => {
  const store = createStore(initialState);
  const { update, getState } = store;

  const certsyAnalytics = createCertsyAnalytics(store, analyticsService);

  const userDetailsUpdated = (props: UserProps) =>
    update(userReducer(getState(), props));

  const pageUpdated = (props: PageState) =>
    update(pageReducer(getState(), props));

  const setProfileActionOriginInState = (origin?: AnalyticsActionOrigin) => {
    const updatedOrigin = origin ? { actionOrigin: origin } : {};
    update(actionOriginReducer(getState(), updatedOrigin));
  };

  const profileLoaded = (
    props: ProfileState,
    params: {
      trackingId: string;
      _id: string;
      externalId?: string;
      isResumeParsed: boolean;
      licencesExpiredVerifiedTotal: number;
      licencesNotVerifiedTotal: number;
      licencesUnverifiableTotal: number;
      licencesVerifiedTotal: number;
      isUnifiedSite: boolean;
      credentialsNotVerifiedTotal?: number;
      credentialsNotVerifiedType?: string;
      credentialsVerifiedTotal?: number;
      credentialsVerifiedType?: string;
      profileCareerHistoryCount?: number;
    },
  ) => {
    const { trackingId, _id, externalId, isUnifiedSite, ...restParams } =
      params;
    userDetailsUpdated({
      authenticated: true,
      trackingId,
      externalId,
      _id,
      isUnifiedSite,
    });

    update(profileReducer(getState(), props));

    update(
      pageReducer(getState(), {
        currentPage: PROFILE_PAGE,
      }),
    );
    const updatedState = getState();

    const data: AnalyticsViewData = {
      currentPage: 'profile',
      eventName: 'profile_displayed',
    };

    analyticsService.view('profile_displayed', {
      ...updatedState,
      ...data,
      ...restParams,
    });
  };

  const createResumeLoaded = (
    { currentPage, eventName }: CreateResumeDisplayed,
    params: {
      trackingId: string;
      _id: string;
      externalId?: string;
      isUnifiedSite: boolean;
    },
  ) => {
    const { trackingId, _id, externalId, isUnifiedSite } = params;
    userDetailsUpdated({
      authenticated: true,
      trackingId,
      externalId,
      _id,
      isUnifiedSite,
    });

    analyticsService.view('create_resume_displayed', {
      ...getState(),
      eventName,
      currentPage,
    });
  };

  const profileCreateResumeButtonClicked = () => {
    analyticsService.link('create_resume_pressed', {
      ...getState(),
      eventName: 'create_resume_pressed',
    });
  };

  const getStartedButtonClicked = () => {
    analyticsService.link('create_resume_pressed', {
      ...getState(),
      eventName: 'create_resume_pressed',
    });
  };

  const createResumeButtonClicked = () => {
    analyticsService.link('create_resume_pressed', {
      ...getState(),
      currentPageSection: 'resume',
      eventName: 'create_resume_pressed',
    });
  };

  const createResumeManageResumesViewed = () => {
    analyticsService.link('create_resume_displayed', {
      ...getState(),
      eventName: 'create_resume_displayed',
      currentPage: 'create resume',
      currentPageSection: 'resume',
    });
  };

  const createAnotherButtonClicked = () => {
    analyticsService.link('create_resume_pressed', {
      ...getState(),
      currentPageSection: 'resume',
      eventName: 'create_resume_pressed',
    });
  };

  const cardOpened = ({
    origin,
    currentPage,
    currentPageSection,
    currentPageSectionState,
    currentPageSubsection,
    customProps,
  }: CardOpenProps) => {
    const actionOrigin = origin
      ? origin
      : getState().actionOrigin ?? 'main card';

    analyticsService.link('profile_section_displayed', {
      ...getState(),
      actionOrigin,
      eventName: 'profile_section_displayed',
      currentPage: currentPage ?? getState().currentPage,
      currentPageSection,
      currentPageSectionState,
      ...(currentPageSubsection && { currentPageSubsection }),
      ...customProps,
    });
  };

  const yearsOfExperienceEditLoaded = ({
    currentPageSectionState,
  }: AnalyticsEditLoadedType) => {
    cardOpened({
      origin: 'main card',
      currentPageSection: 'years of experience',
      currentPageSectionState,
    });
  };

  const unconfirmedDataEditLoadedFromApplySuccess = ({
    currentPageSection,
    currentPageSectionState,
  }: {
    currentPageSection: AnalyticsCurrentPageSection;
    currentPageSectionState: AnalyticsCurrentPageSectionState;
  }) => {
    cardOpened({
      origin: 'application form',
      currentPageSection,
      currentPageSectionState,
    });
  };

  const profileVisibilityUpdate = (
    profileVisibilityLevel: AnalyticsProfileVisibilityLevel,
    actionOrigin?: AnalyticsActionOrigin,
  ) => {
    update(profileReducer(getState(), { profileVisibilityLevel }));

    updateCard({
      currentPageSection: 'visibility settings',
      actionName: 'EDIT',
      actionOrigin,
      customProps: {
        profileVisibilityLevel,
        currentPageSubsection: 'visibility',
      },
    });
  };

  const updateCard = ({
    actionName,
    currentPageSection,
    customProps,
    actionOrigin: origin,
  }: UpdateCardProps) => {
    const actionOrigin = origin
      ? origin
      : getState().actionOrigin ?? 'main card';

    if (customProps && 'profileApproachabilityStatus' in customProps) {
      update(
        profileReducer(getState(), {
          profileApproachabilityStatus:
            customProps.profileApproachabilityStatus,
        }),
      );
    }

    analyticsService.link('profile_update_succeeded', {
      ...getState(),
      eventName: 'profile_update_succeeded',
      updateType: actionName.toLowerCase() as AnalyticsUpdateType,
      currentPageSection,
      actionOrigin,
      ...customProps,
    });
  };

  const updateUnconfirmedCard = ({
    actionName,
    actionOrigin,
    currentPageSection,
  }: UpdateUnconfirmedCardProps) => {
    const eventName =
      actionName === REJECT
        ? 'nudge_dismiss_pressed'
        : 'profile_update_succeeded';

    analyticsService.link(eventName, {
      ...getState(),
      currentPageSection,
      eventName,
      actionOrigin,
      ...(actionName === REJECT
        ? { nudgeType: 'resume parsing' }
        : {
            updateType: actionName.toLowerCase() as AnalyticsUpdateType,
          }),
    });
  };

  const trackRightToWorkDeletePressed = () => {
    analyticsService.link('delete_right_to_work_pressed', {
      ...getState(),
      eventName: 'delete_right_to_work_pressed',
      currentPage: 'profile',
      currentPageSection: 'next role',
      actionOrigin: 'main card',
      currentPageSubsection: 'right to work',
    });
  };

  const rightToWorkAddCountryPressed = () => {
    analyticsService.link('profile_add_country_pressed', {
      ...getState(),
      eventName: 'profile_add_country_pressed',
      currentPage: 'profile',
      currentPageSection: 'next role',
      currentPageSubsection: 'right to work',
      currentPageSectionState: 'edit',
    });
  };

  const resumeUpdated = (props: ManageResumeUpdateProps) => {
    const { eventName } = props;
    analyticsService.link(eventName, {
      ...getState(),
      ...props,
    });
  };

  const resumeParsedDataLinkClicked = ({
    eventName,
    parsedDataSection,
    currentPageSection,
  }: {
    eventName: AnalyticsLinkEvent;
    parsedDataSection: Extract<
      AnalyticsCurrentPageSection,
      'career history' | 'education'
    >;
    currentPageSection: AnalyticsCurrentPageSection;
  }) => {
    analyticsService.link(eventName, {
      ...getState(),
      eventName,
      parsedDataSection,
      currentPageSection,
    });
  };

  const resumeDeletePressed = ({
    eventName,
    isResumeDefault,
    updateType,
    resumeType,
  }: {
    eventName: AnalyticsLinkEvent;
    isResumeDefault: boolean;
    updateType: AnalyticsUpdateType;
    resumeType: AnalyticsResumeType;
  }) => {
    analyticsService.link(eventName, {
      ...getState(),
      isResumeDefault,
      updateType,
      resumeType,
    });
  };

  const resumeAlertViewed = ({
    alertShownType,
    alertShownTone,
    alertShownPosition,
    alertShownTitle,
    alertShownMessage,
    profileState,
  }: {
    alertShownType: AnalyticsResumeAlertShownType;
    alertShownTone: AnalyticsResumeAlertShownTone;
    alertShownPosition: AnalyticsResumeAlertPosition;
    alertShownTitle: AnalyticsResumeAlertTitle;
    alertShownMessage: AnalyticsResumeAlertShownMessage;
    profileState?: ProfileState;
  }) => {
    if (profileState) {
      update(profileReducer(getState(), profileState));
    }
    analyticsService.link('alert_viewed', {
      ...getState(),
      alertShownType,
      alertShownTone,
      alertShownPosition,
      alertShownTitle,
      alertShownMessage,
    });
  };

  const resumeSensitiveInfoDialogDisplayed = ({
    eventName,
    isResumeDefault,
    resumeType,
  }: {
    eventName: AnalyticsLinkEvent;
    isResumeDefault: boolean;
    resumeType: AnalyticsResumeType;
  }) => {
    analyticsService.link(eventName, {
      ...getState(),
      isResumeDefault,
      resumeType,
    });
  };

  const resumeSensitiveInfoDialogPressed = ({
    eventName,
    linkText,
  }: {
    eventName: AnalyticsLinkEvent;
    linkText: AnalyticsSensitiveInformationLinkText;
  }) => {
    analyticsService.link(eventName, {
      ...getState(),
      linkText,
    });
  };

  const companyReviewsClicked = ({
    linkNameEventName,
    companyName,
    currentPageSection,
  }: {
    linkNameEventName: AnalyticsLinkEvent;
    companyName: string;
    currentPageSection: AnalyticsCurrentPageSection;
  }) => {
    analyticsService.link(linkNameEventName, {
      ...getState(),
      eventName: linkNameEventName,
      companyName,
      currentPageSection,
    });
  };

  const linkClicked = ({
    linkNameEventName,
    thirdPartyLink,
    linkPosition,
    type,
  }: {
    linkNameEventName: AnalyticsLinkEvent;
    thirdPartyLink: boolean;
    linkPosition: AnalyticsLinkPosition;
    type: string;
  }) => {
    const linkType = type ? { linkType: type } : {};
    analyticsService.link(linkNameEventName, {
      ...getState(),
      ...linkType,
      thirdPartyLink,
      linkPosition,
    });
  };

  const headerFooterLinkClicked = ({
    linkText,
    linkAction,
    linkPosition,
    linkNameEventName,
  }: AnalyticsHeaderFooterLinkProps & {
    linkNameEventName: AnalyticsLinkEvent;
  }) => {
    analyticsService.link(linkNameEventName, {
      ...getState(),
      eventName: linkNameEventName,
      linkText,
      linkPosition,
      ...(linkAction ? { linkAction } : {}),
    });
  };

  const profileInsightsAnalyticsTracker = ({
    eventName,
    props,
    profileState,
  }: {
    eventName: AnalyticsLinkEvent;
    props?: AnalyticsProfileInsightsProps;
    profileState?: ProfileState;
  }) => {
    if (profileState) {
      update(profileReducer(getState(), profileState));
    }

    const updatedState = getState();

    analyticsService.link(eventName, {
      ...updatedState,
      eventName,
      ...props,
    });
  };

  const profileSuggestionsAnalyticsTracker = ({
    eventName,
    props,
    profileState,
  }: {
    eventName: AnalyticsLinkEvent;
    props?: AnalyticsProfileSuggestionsProps;
    profileState?: ProfileState;
  }) => {
    if (profileState) {
      update(profileReducer(getState(), profileState));
    }

    const updatedState = getState();

    analyticsService.link(eventName, {
      ...updatedState,
      eventName,
      ...props,
    });
  };

  const shareableProfileButtonPressed = ({
    isEditState,
    eventName,
  }: {
    isEditState: boolean;
    eventName: AnalyticsLinkEvent;
  }) => {
    analyticsService.link(eventName, {
      ...getState(),
      eventName,
      ...(isEditState
        ? {
            currentPageSection: 'visibility settings',
            currentPageSectionState: 'edit',
            currentPageSubsection: 'shared profile',
            actionOrigin: 'main card',
            updateType: 'add',
          }
        : {}),
    });
  };

  const shareableProfileTogglePressed = ({
    active,
    isShareableProfileCreated,
  }: {
    active: boolean;
    isShareableProfileCreated: boolean;
  }) => {
    const eventName = 'profile_update_succeeded';
    const profileSharedStatus = getProfileSharedStatus({
      isShareableProfileCreated,
      isShareableProfileActive: active,
    });

    update(profileReducer(getState(), { profileSharedStatus }));

    analyticsService.link(eventName, {
      ...getState(),
      eventName,
      toggleType: 'shareable profile',
      profileSharedStatus,
      currentPageSection: 'visibility settings',
      currentPageSectionState: 'edit',
      currentPageSubsection: 'shared profile',
      actionOrigin: 'main card',
      updateType: 'edit',
    });
  };

  const shareableProfileViewLoaded = ({
    isSelfView,
  }: {
    isSelfView: boolean;
  }) => {
    const eventName = 'profile_displayed';

    analyticsService.view(eventName, {
      ...getState(),
      eventName,
      currentPage: 'shareable profile',
      isSelfView,
    });
  };

  const nudgeTracker = ({
    eventName,
    nudgeType,
    callToAction,
    callToActionType,
  }: {
    eventName: AnalyticsLinkEvent;
    nudgeType: AnalyticsNudgeType;
    callToAction: AnalyticsCallToAction;
    callToActionType: AnalyticsCallToActionType;
  }) => {
    analyticsService.link(eventName, {
      ...getState(),
      nudgeType,
      callToAction,
      callToActionType,
    });
  };

  const shareableProfileShareLinkClicked = ({
    eventName,
    currentPage,
    profileSharedStatus,
    actionOrigin,
    updateType,
  }: {
    eventName: AnalyticsLinkEvent;
    currentPage: AnalyticsPage;
    profileSharedStatus: AnalyticsProfileSharedStatus;
    actionOrigin: AnalyticsActionOrigin;
    updateType?: AnalyticsUpdateType;
  }) => {
    analyticsService.link(eventName, {
      ...getState(),
      eventName,
      currentPageSection: 'visibility settings',
      currentPageSubsection: 'shared profile',
      currentPage,
      actionOrigin,
      profileSharedStatus,
      ...(updateType ? { updateType } : {}),
    });
  };

  const shareableProfileShareViewed = ({
    eventName,
    currentPage,
    profileSharedStatus,
  }: {
    eventName: AnalyticsViewEvent;
    currentPage: AnalyticsPage;
    profileSharedStatus: AnalyticsProfileSharedStatus;
  }) => {
    analyticsService.view(eventName, {
      ...getState(),
      eventName,
      currentPageSection: 'visibility settings',
      currentPageSubsection: 'shared profile',
      actionOrigin: 'profile',
      currentPage,
      profileSharedStatus,
    });
  };

  const shareableProfileVerifiedIdentityDialogViewed = ({
    isSelfView,
  }: {
    isSelfView: boolean;
  }) => {
    const eventName = 'profile_section_displayed';

    analyticsService.view(eventName, {
      ...getState(),
      eventName,
      currentPage: 'shareable profile',
      currentPageSection: 'verified identity',
      isSelfView,
    });
  };

  // Verifications:
  const verificationsWidgetViewed = ({
    widgetName,
  }: {
    widgetName?: AnalyticsVerificationsWidgetName;
  }) => {
    const eventName = 'profile_verifications_widget_viewed';
    analyticsService.link(eventName, {
      ...getState(),
      eventName,
      currentPage: 'profile',
      ...(widgetName ? { widgetName } : {}),
    });
  };

  const verificationsWidgetClicked = ({
    widgetName,
    callToAction,
    linkText,
  }: {
    widgetName?: AnalyticsVerificationsWidgetName;
    callToAction: AnalyticsVerificationsWidgetCallToAction;
    linkText: AnalyticsVerificationsWidgetLinkText;
  }) => {
    const eventName = 'profile_verifications_widget_pressed';
    analyticsService.link(eventName, {
      ...getState(),
      eventName,
      currentPage: 'profile',
      ...(widgetName ? { widgetName } : {}),
      callToAction,
      linkText,
    });
  };

  type VerificationsDrawerOpenedProps =
    | {
        currentPageSubsection: 'your verifications';
        credentialsVerifiedTotal: number | undefined;
        credentialsVerifiedType: string | undefined;
      }
    | {
        currentPageSubsection: 'available verifications';
        credentialsNotVerifiedTotal: number | undefined;
        credentialsNotVerifiedType: string | undefined;
      };

  const verificationsDrawerOpened = (props: VerificationsDrawerOpenedProps) => {
    const eventName = 'profile_section_displayed';
    analyticsService.view(eventName, {
      ...getState(),
      eventName,
      currentPage: 'profile',
      currentPageSection: 'verifications',
      ...props,
    });
  };

  const seekPassDialogDisplayed = ({
    origin,
    certsyType,
    entityName,
  }: {
    origin: VerificationsSeekPassDialogAnalyticsOrigin;
    certsyType: AnalyticsVerifiableCredentialType;
    entityName?: string;
  }) => {
    const eventName = 'certsy_dialog_displayed';

    analyticsService.view(eventName, {
      ...getState(),
      eventName,
      currentPage: 'profile',
      ...(origin === 'available verifications'
        ? {
            currentPageSection: 'verifications',
            currentPageSubsection: 'available verifications',
          }
        : {}),
      certsyType: entityName ? `${certsyType} - ${entityName}` : certsyType,
    });
  };

  const seekPassDialogClicked = ({
    certsyType,
    entityName,
    callToAction,
    origin,
  }: {
    certsyType: AnalyticsVerifiableCredentialType;
    entityName?: string;
    callToAction: AnalyticsVerificationsSeekPassDialogCallToAction;
    origin: VerificationsSeekPassDialogAnalyticsOrigin;
  }) => {
    const eventName = 'certsy_dialog_pressed';
    analyticsService.link(eventName, {
      ...getState(),
      eventName,
      currentPage: 'profile',
      ...(origin === 'available verifications'
        ? {
            currentPageSection: 'verifications',
            currentPageSubsection: 'available verifications',
          }
        : {}),
      certsyType: entityName ? `${certsyType} - ${entityName}` : certsyType,
      callToAction,
    });
  };

  const seekPassManageClicked = () => {
    const eventName = 'certsy_manage_pressed';
    analyticsService.link(eventName, {
      ...getState(),
      eventName,
      currentPage: 'profile',
      currentPageSection: 'verifications',
      currentPageSubsection: 'your verifications',
    });
  };

  const seekPassVerifyClicked = ({
    certsyType,
    entityName,
    expiryStatus,
  }: {
    certsyType: AnalyticsVerifiableCredentialType;
    entityName?: string;
    expiryStatus?: CredentialExpiryStatus;
  }) => {
    const eventName =
      expiryStatus === CredentialExpiryStatus.Expired ||
      expiryStatus === CredentialExpiryStatus.ExpiringSoon
        ? 'reverify_pressed'
        : 'certsy_verify_pressed';
    analyticsService.link(eventName, {
      ...getState(),
      eventName,
      currentPage: 'profile',
      currentPageSection: 'verifications',
      currentPageSubsection: 'available verifications',
      certsyType: entityName ? `${certsyType} - ${entityName}` : certsyType,
      ...(expiryStatus
        ? { certsyStatus: mapExpiryStatusToCertsyStatusAnalytics(expiryStatus) }
        : {}),
    });
  };

  const seekPassLearnMoreClicked = () => {
    const eventName = 'certsy_learn_more_pressed';
    analyticsService.link(eventName, {
      ...getState(),
      eventName,
      currentPage: 'profile',
      currentPageSection: 'verifications',
      currentPageSubsection: 'available verifications',
    });
  };

  const verificationsTabClicked = ({
    eventName,
    currentPageSubsection,
  }: {
    eventName:
      | 'profile_your_verifications_tab_pressed'
      | 'profile_available_verifications_tab_pressed';
    currentPageSubsection: AnalyticsVerificationsCurrentPageSubsection;
  }) => {
    analyticsService.link(eventName, {
      ...getState(),
      eventName,
      currentPage: 'profile',
      currentPageSection: 'verifications',
      currentPageSubsection,
    });
  };

  const seekPassInReviewClicked = () => {
    const eventName = 'certsy_in_review_pressed';
    analyticsService.link(eventName, {
      ...getState(),
      eventName,
      currentPage: 'profile',
      currentPageSection: 'verifications',
      currentPageSubsection: 'available verifications',
    });
  };

  return {
    getState,
    updateCard,
    updateUnconfirmedCard,
    cardOpened,
    userDetailsUpdated,
    pageUpdated,
    profileLoaded,
    createResumeLoaded,
    profileCreateResumeButtonClicked,
    getStartedButtonClicked,
    createResumeButtonClicked,
    createResumeManageResumesViewed,
    createAnotherButtonClicked,
    yearsOfExperienceEditLoaded,
    resumeUpdated,
    resumeParsedDataLinkClicked,
    resumeDeletePressed,
    resumeAlertViewed,
    resumeSensitiveInfoDialogDisplayed,
    resumeSensitiveInfoDialogPressed,
    unconfirmedDataEditLoadedFromApplySuccess,
    profileInsightsAnalyticsTracker,
    profileSuggestionsAnalyticsTracker,
    profileVisibilityUpdate,
    linkClicked,
    headerFooterLinkClicked,
    companyReviewsClicked,
    setProfileActionOriginInState,
    ...certsyAnalytics,
    shareableProfileButtonPressed,
    shareableProfileTogglePressed,
    shareableProfileViewLoaded,
    nudgeTracker,
    shareableProfileShareLinkClicked,
    shareableProfileShareViewed,
    shareableProfileVerifiedIdentityDialogViewed,
    verificationsWidgetViewed,
    verificationsWidgetClicked,
    verificationsDrawerOpened,
    seekPassDialogDisplayed,
    seekPassManageClicked,
    seekPassVerifyClicked,
    seekPassLearnMoreClicked,
    verificationsTabClicked,
    seekPassDialogClicked,
    seekPassInReviewClicked,
    trackRightToWorkDeletePressed,
    rightToWorkAddCountryPressed,
    updateAnalyticsState: update,
  };
};

const analyticsClient = createAnalytics();

export { analyticsClient, createAnalytics, defaultState };
